import { z } from "zod";

import { ActionDefinitionSchema } from "../../../../schemas/AccessCard/actionDefinition.schema";
import { CardVariablesSchema } from "../../../../schemas/AccessCard/cardVariable.schema";
import { AccessCardPoliciesSchema } from "../../../../schemas/AccessCard/policies.schema";

// ---------------------------------------------------------------------------
// Shared sub-schemas
// ---------------------------------------------------------------------------

const AccessCardCreatorSchema = z.object({
  id: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable()
});

export const AccessCardItemSchema = z.object({
  id: z.string(),
  credentialId: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  isMaster: z.boolean(),
  policies: AccessCardPoliciesSchema,
  variables: CardVariablesSchema.default([]),
  color: z.string().default("slate"),
  createdBy: AccessCardCreatorSchema.nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});

// ---------------------------------------------------------------------------
// List (by credential)
// ---------------------------------------------------------------------------

export const AccessCardListParamsSchema = z
  .object({
    /** Unified Credential ID — use this when coming from the credentials module */
    credentialId: z.string().min(1).optional(),
    /** ToolCredentials ID — use this when coming from workflow nodes */
    toolCredentialsId: z.string().min(1).optional()
  })
  .refine(
    (p) => p.credentialId || p.toolCredentialsId,
    "Either credentialId or toolCredentialsId is required"
  );

export const AccessCardListResponseSchema = z.object({
  accessCards: z.array(AccessCardItemSchema)
});

// ---------------------------------------------------------------------------
// List All (org-wide or per-tool)
// ---------------------------------------------------------------------------

export const AccessCardListAllParamsSchema = z.object({
  /** Filter to cards for a specific tool (AgentExternalTool.id) */
  toolId: z.string().min(1).optional()
});

export const AccessCardListAllItemSchema = AccessCardItemSchema.extend({
  /** ToolCredentials ID — needed to set toolCredentialId on workflow nodes */
  toolCredentialsId: z.string().nullable(),
  credential: z.object({
    service: z.string(),
    serviceName: z.string(),
    serviceImageUrl: z.string().nullable(),
    source: z.string(),
    /** Third-party account identifier (email / username / apn_* / etc). Distinguishes cards when an org has multiple accounts for the same service. */
    accountIdentifier: z.string().nullable()
  })
});

export const AccessCardListAllResponseSchema = z.object({
  accessCards: z.array(AccessCardListAllItemSchema)
});

// ---------------------------------------------------------------------------
// Get
// ---------------------------------------------------------------------------

export const AccessCardGetPathVarsSchema = z.object({
  id: z.string()
});

// ---------------------------------------------------------------------------
// Create
// ---------------------------------------------------------------------------

export const AccessCardCreateBodySchema = z.object({
  credentialId: z.string().min(1),
  name: z.string().trim().min(1).max(255),
  description: z.string().max(1000).nullable().optional(),
  policies: AccessCardPoliciesSchema,
  variables: CardVariablesSchema.optional().default([]),
  color: z.string().optional()
});

// ---------------------------------------------------------------------------
// Update
// ---------------------------------------------------------------------------

export const AccessCardUpdatePathVarsSchema = z.object({
  id: z.string()
});

export const AccessCardUpdateBodySchema = z.object({
  name: z.string().trim().min(1).max(255).optional(),
  description: z.string().max(1000).nullable().optional(),
  policies: AccessCardPoliciesSchema.optional(),
  variables: CardVariablesSchema.optional(),
  color: z.string().optional()
});

// ---------------------------------------------------------------------------
// Delete
// ---------------------------------------------------------------------------

export const AccessCardDeletePathVarsSchema = z.object({
  id: z.string()
});

export const AccessCardDeleteResponseSchema = z.object({
  deleted: z.literal(true)
});

// ---------------------------------------------------------------------------
// Available Actions (dynamic resolution from tool metadata)
// ---------------------------------------------------------------------------

export const AvailableActionsParamsSchema = z.object({
  credentialId: z.string().min(1)
});

export const AvailableActionsResponseSchema = z.object({
  actions: z.array(ActionDefinitionSchema)
});
